import type { RefObject } from 'react'
import type { PageTransitionInfo } from './types'

// Assignments for better intellisense
type Refs = Record<string, unknown>
type GlobalRefs = Record<string, unknown>

// Arguments passed to the transition function
type TransitionFnArgs<R extends Refs, GR extends GlobalRefs> = {
  done: () => void
  refs: Partial<WrapInRef<R>>
  globalRefs: Partial<WrapInRef<GR>>
  info: PageTransitionInfo
}

// The transition function
type TransitionFn<R extends Refs, GR extends GlobalRefs> = (
  args: TransitionFnArgs<R, GR>
) => void

// Utility to wrap the refs in a RefObject so you can just define the type of the ref and it'll be wrapped in a RefObject
type WrapInRef<T extends Record<string, unknown>> = {
  [K in keyof T]: RefObject<T[K]>
}

// Transition function factory helper
export function createTransition<R extends Refs, GR extends GlobalRefs>(
  transitionFn: TransitionFn<R, GR>
) {
  return (
    refs: Partial<WrapInRef<R>>,
    globalRefs: Partial<WrapInRef<GR>>,
    info: PageTransitionInfo
  ) => {
    return new Promise((resolve) => {
      transitionFn({ done: () => resolve(true), refs, globalRefs, info })
    })
  }
}
